import React, {useEffect, useState} from 'react';
import {OrderUpdateDTO, OrderViewDTO, schemaOrderUpdate} from "../types/dto";
import {Dialog, DialogActions, DialogContent, DialogProps, DialogTitle, IconButton, Tooltip} from "@mui/material";
import {useForm} from "react-hook-form";
import {zodResolver} from "@hookform/resolvers/zod";
import {OrderFields} from "../types/fields";
import {OrderStatusTypes} from "../types/enum";
import {HttpOrders} from "../api/HttpOrders";
import toast from "react-hot-toast";
import Grid from "@mui/material/Grid";
import {Button, ControlledDatePicker, ControlledTextField} from "../../../components/forms";
import OrderDetailCard from "./OrderDetailCard"; // Importamos el componente de vista
import EditIcon from '@mui/icons-material/Edit';
import VisibilityIcon from '@mui/icons-material/Visibility';

type DialogMode = 'create' | 'edit' | 'view';

interface OrderDialogProps extends DialogProps {
    order?: OrderViewDTO,
    onClose: () => void,
    onAfterSubmit: () => void,
}

function OrderDialog({order, onClose, onAfterSubmit, ...dialogProps}: OrderDialogProps) {
    const [mode, setMode] = useState<DialogMode>(() => {
        return order ? 'view' : 'create';
    });

    const {control, handleSubmit, reset, setFocus} = useForm<OrderUpdateDTO>({
        resolver: zodResolver(schemaOrderUpdate),
        defaultValues: {
            ...order,
            [OrderFields.StatusCode]: order?.[OrderFields.StatusCode] || OrderStatusTypes.PENDING,
            [OrderFields.DeliveryDate]: order?.[OrderFields.DeliveryDate] || new Date()
        }
    });

    const handleModeChange = (newMode: DialogMode) => {
        setMode(newMode);
        if (newMode === 'view') {
            reset({...order, [OrderFields.DeliveryDate]: order?.[OrderFields.DeliveryDate] || new Date()});
        }
    };

    const submitOrder = (data: OrderUpdateDTO) => {
        if (mode === 'edit' && order) {
            return HttpOrders.put(order.id, data);
        }

        return HttpOrders.post(data);
    };

    const onSubmitWrapper = () => {
        let mensajeError = `No se pudo guardar la entrega`;
        const promise = new Promise<void>((resolve, reject) => {
            handleSubmit(
                (formData) => {
                    submitOrder(formData)
                        .then((response) => {
                            if (response.tieneError) {
                                mensajeError = response.descripcionError;
                                reject(response.descripcionError);
                            } else {
                                setTimeout(onAfterSubmit, 500);
                                resolve();
                            }
                        })
                        .catch(reject);
                },
                (validationErrors) => {
                    mensajeError = 'Revise el formulario';
                    reject(JSON.stringify(validationErrors));
                }
            )();
        });
        toast.promise(promise, {
            success: 'Guardado',
            error: () => mensajeError,
            loading: 'Guardando'
        });

        return promise;
    };

    const getTitle = () => {
        if (mode === 'create') return 'Nueva Entrega';
        if (mode === 'edit') return 'Editar Entrega';

        return 'Detalle de Entrega';
    }

    useEffect(() => {
        if (mode === 'edit' || mode === 'create') {
            // el timeout es una trampa para que esto funcione, sin el timeout no aplica el autoFocus
            const timer = setTimeout(() => {
                setFocus(OrderFields.Receiver);
            }, 10);

            return () => clearTimeout(timer);
        }

    }, [setFocus, mode]);

    return (
        <Dialog {...dialogProps} onClose={onClose} fullWidth maxWidth="md">
            <DialogTitle sx={{display: 'flex', justifyContent: 'space-between', alignItems: 'center'}}>
                {getTitle()}
                {order && (
                    mode === 'view' ? (
                        <Tooltip title="Editar">
                            <IconButton
                                color={'primary'}
                                onClick={() => handleModeChange('edit')}>
                                <EditIcon/>
                            </IconButton>
                        </Tooltip>
                    ) : mode === 'edit' && (
                        <Tooltip title="Ver">
                            <IconButton
                                color={'primary'}
                                onClick={() => handleModeChange('view')}>
                                <VisibilityIcon/>
                            </IconButton>
                        </Tooltip>
                    )
                )}
            </DialogTitle>
            <DialogContent sx={{p: 2}}>
                {mode === 'view' && order ? (
                    <OrderDetailCard order={order}/>
                ) : (
                    <Grid container spacing={2} sx={{mt: 1}}>
                        <Grid item xs={12} sm={6}>
                            <ControlledTextField control={control} name={OrderFields.Receiver} label={'Destinatario'}
                                                 fullWidth/>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <ControlledTextField control={control} name={OrderFields.Address} label={'Dirección'}
                                                 fullWidth/>
                        </Grid>
                        <Grid item xs={12}>
                            <ControlledTextField control={control} name={OrderFields.Items} label={'Materiales'}
                                                 fullWidth multiline rows={6}/>
                        </Grid>
                        <Grid item xs={12} sm={4}>
                            <ControlledDatePicker control={control} name={OrderFields.DeliveryDate}
                                                  label={'Fecha de Entrega'}/>
                        </Grid>
                        <Grid item xs={12} sm={4}>
                            <ControlledTextField control={control} name={OrderFields.DeliveryNote}
                                                 label={'Nro. Remito'} fullWidth/>
                        </Grid>
                        <Grid item xs={12} sm={4}>
                            <ControlledTextField control={control} name={OrderFields.InvoiceNumber}
                                                 label={'Nro. Factura'} fullWidth/>
                        </Grid>
                        <Grid item xs={12}>
                            <ControlledTextField control={control} name={OrderFields.Observations}
                                                 label={'Observaciones / Devoluciones'}
                                                 fullWidth multiline rows={3}/>
                        </Grid>
                    </Grid>
                )}
            </DialogContent>
            <DialogActions>
                {mode === 'view' ? (
                    <Button variant='text' color='primary' onClick={onClose}>
                        Cerrar
                    </Button>
                ) : (
                    <>
                        <Button variant='text' color='secondary' onClick={onClose}>
                            Cancelar
                        </Button>
                        <Button variant='contained' color='primary' promise={onSubmitWrapper}>
                            Guardar
                        </Button>
                    </>
                )}
            </DialogActions>
        </Dialog>
    );
}

export default OrderDialog;